import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../components/Decor';
import { api } from '../api';
import type { Enrollment, PublicMember, SiteContent } from '../shared/types';
import {
  Articles, Clips, Courses, Footer, Header, Hero, HoroAndLive, Join, Journey, LatestNews, LoginDialog, Pillars, goJoin, useToast, type Auth,
} from '../App';
import {
  ArticleDetailPage, ArticlesPage, ClipsPage, CourseDetailPage, CoursesPage, HoroscopePage, MemberPage, NewsDetailPage, NewsPage, NotFoundPage, PracticePage, PrivacyPage, pageText,
} from './pages';
import { usePath } from './router';

const TITLES: Record<string, string> = {
  '/courses': 'คอร์สสอนเทรด', '/horoscope': 'เช็กดวงการเทรด', '/black-trade': 'Black Trade', '/demo-trade': 'Demo Trade',
  '/clips': 'คลังวิดีโอ', '/articles': 'บทความ', '/news': 'ข่าวสาร', '/member': 'สมาชิก', '/privacy': 'นโยบายความเป็นส่วนตัว',
};

export default function Site() {
  const path = usePath();
  const { msg, notify, say, close } = useToast();

  // null = no server (dist-single / static hosting) → pages fall back to built-in copy.
  const [content, setContent] = useState<SiteContent | null>(null);
  const [member, setMember] = useState<PublicMember | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [online, setOnline] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const loadContent = useCallback(() => {
    void api<SiteContent>('GET', '/api/public/content').then((r) => { if (r.ok) setContent(r.data); });
  }, []);
  const loadMe = useCallback(() => {
    void api<{ member: PublicMember | null; enrollments: Enrollment[] }>('GET', '/api/member/me').then((r) => {
      if (!r.ok) return;
      setOnline(true);
      setMember(r.data.member);
      setEnrollments(r.data.enrollments ?? []);
    });
  }, []);
  useEffect(() => {
    loadContent();
    loadMe();
  }, [loadContent, loadMe]);
  // Members-only clips/articles unlock after login, so refetch content when the session changes.
  const memberId = member?.id ?? '';
  useEffect(() => { if (online) loadContent(); }, [memberId, online, loadContent]);

  const closeLogin = useCallback(() => setLoginOpen(false), []);
  const auth: Auth = {
    member,
    enrollments,
    setEnrollments,
    online,
    toast: say,
    openLogin: () => setLoginOpen(true),
    signedIn: (m) => {
      setMember(m);
      setLoginOpen(false);
      loadMe(); // pick up the member's courses
      say(`ยินดีต้อนรับ คุณ${m.name}`);
    },
    logout: () => {
      void api('POST', '/api/member/logout').then(() => {
        setMember(null);
        setEnrollments([]);
        say('ออกจากระบบแล้ว');
      });
    },
  };

  const lineAddUrl = content?.lineAddUrl ?? '';
  const seg = path.replace(/\/+$/, '').split('/').filter(Boolean);
  const onLocked = () => (auth.online ? auth.openLogin() : goJoin());

  let page: JSX.Element;
  let title = TITLES[`/${seg[0] ?? ''}`] ?? '';
  if (seg.length === 0) {
    title = '';
    page = (
      <>
        <Hero />
        <Pillars notify={notify} />
        <Courses courses={content ? content.courses : null} title={pageText(content, 'courses').title} sub={pageText(content, 'courses').subtitle} />
        <HoroAndLive notify={notify} auth={auth} />
        <LatestNews news={content ? content.news : null} />
        <Journey notify={notify} />
        <Clips clips={content ? content.clips : null} onLocked={onLocked} />
        <Articles articles={content ? content.articles : null} />
        <Join notify={notify} privacy={content?.privacy ?? null} auth={auth} courses={content?.courses ?? []} />
      </>
    );
  } else if (seg[0] === 'courses' && seg.length === 1) page = <CoursesPage content={content} />;
  else if (seg[0] === 'courses' && seg.length === 2) {
    const c = content?.courses.find((x) => x.id === seg[1]);
    if (c) title = c.title;
    page = <CourseDetailPage content={content} id={seg[1]} lineAddUrl={lineAddUrl} auth={auth} notify={notify} />;
  } else if (seg[0] === 'horoscope' && seg.length === 1) page = <HoroscopePage content={content} notify={notify} auth={auth} />;
  else if ((seg[0] === 'black-trade' || seg[0] === 'demo-trade') && seg.length === 1) page = <PracticePage content={content} kind={seg[0]} notify={notify} />;
  else if (seg[0] === 'clips' && seg.length === 1) page = <ClipsPage content={content} auth={auth} />;
  else if (seg[0] === 'articles' && seg.length === 1) page = <ArticlesPage content={content} />;
  else if (seg[0] === 'articles' && seg.length === 2) {
    const a = content?.articles.find((x) => x.id === seg[1]);
    if (a) title = a.title;
    page = <ArticleDetailPage content={content} id={seg[1]} auth={auth} />;
  } else if (seg[0] === 'news' && seg.length === 1) page = <NewsPage content={content} />;
  else if (seg[0] === 'news' && seg.length === 2) {
    const n = content?.news.find((x) => x.id === seg[1]);
    if (n) title = n.title;
    page = <NewsDetailPage content={content} id={seg[1]} />;
  } else if (seg[0] === 'member' && seg.length === 1) page = <MemberPage content={content} notify={notify} auth={auth} />;
  else if (seg[0] === 'privacy' && seg.length === 1) page = <PrivacyPage content={content} />;
  else {
    title = 'ไม่พบหน้านี้';
    page = <NotFoundPage />;
  }

  useEffect(() => {
    document.title = title ? `${title} | โหราเทรดเดอร์` : 'โหราเทรดเดอร์ | Astro Trader';
  }, [title]);

  return (
    <>
      <a className="skip-link" href="#main">ข้ามไปยังเนื้อหา</a>
      <div className="starfield" aria-hidden="true" />
      <Header notify={notify} auth={auth} path={path} />
      <main id="main" tabIndex={-1} key={path}>
        {page}
      </main>
      <Footer notify={notify} auth={auth} lineAddUrl={lineAddUrl} />
      {loginOpen && <LoginDialog onClose={closeLogin} onDone={auth.signedIn} />}
      <div className="toast-region" role="status" aria-live="polite">
        {msg && (
          <div className="toast">
            <Icon name="info" />
            <span>{msg}</span>
            <button type="button" className="btn btn-icon toast-close" aria-label="ปิดข้อความ" onClick={close}><Icon name="close" /></button>
          </div>
        )}
      </div>
    </>
  );
}
